import { useEffect, useRef } from 'react';
import { teamOfSeat, turnOfDeal, type DealRecord, type HouseRules, type ScheduleSlot } from '../game/match';
import type { SeatPlayer, Teams } from '../shared/schemas';
import { HandPips } from './NowDealing';
import { Avatar, Name, SuitGlyph, teamAvatarFill } from './ui';

/** The schedule grouped by dealer turn: one card per turn, tinted by the dealer's team, one dot per hand. */
export function DealerStrip({ slots, seats, teams, rules }: { slots: ScheduleSlot[]; seats: SeatPlayer[]; teams: Teams; rules: HouseRules }) {
  const current = useRef<HTMLLIElement>(null);
  const currentNo = slots.find((s) => s.state === 'current')?.dealNo;
  useEffect(() => {
    current.current?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [currentNo]);

  const turns: ScheduleSlot[][] = [];
  for (const slot of slots) {
    const t = turnOfDeal(slot.dealNo, rules);
    (turns[t] ??= []).push(slot);
  }

  return (
    <ol className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pt-1 pb-3" aria-label="Dealer schedule">
      {turns.filter(Boolean).map((group) => {
        const first = group[0]!;
        const last = group.at(-1)!;
        const p = seats[first.dealerSeat]!;
        const team = teams[teamOfSeat(first.dealerSeat)];
        const isCurrent = group.some((s) => s.state === 'current');
        const done = group.every((s) => s.state === 'done');
        const guaa = group.reduce((n, s) => n + (s.deal ? s.deal.guaaA + s.deal.guaaB : 0), 0);
        const range = group.length > 1 ? `Deals ${first.dealNo}–${last.dealNo}` : `Deal ${first.dealNo}`;
        const status = isCurrent ? 'dealing now' : done ? (guaa ? `${guaa} Guaa` : 'all saved') : 'upcoming';
        return (
          <li
            key={first.dealNo}
            ref={isCurrent ? current : undefined}
            aria-current={isCurrent ? 'step' : undefined}
            aria-label={`${range}: ${p.name} deals, ${status}`}
            className={`flex w-[7.5rem] shrink-0 snap-center flex-col gap-2 rounded-2xl p-2.5 transition ${
              isCurrent ? 'bg-cream text-ink shadow-lg ring-2 ring-gold' : done ? 'text-cream' : 'border border-dashed border-white/20 text-cream/70'
            }`}
            style={done && !isCurrent ? { background: `linear-gradient(165deg, ${team.color}2e, ${team.color}12)`, boxShadow: `inset 0 0 0 1px ${team.color}40` } : undefined}
          >
            <div className="flex items-center gap-2">
              <Avatar
                name={p.name}
                avatarKey={p.avatarKey}
                size={28}
                ring={isCurrent ? team.color : undefined}
                fill={teamAvatarFill(team.color)}
                className={!isCurrent && !done ? 'opacity-60' : ''}
              />
              <Name className="min-w-0 truncate text-sm font-semibold">{p.name}</Name>
            </div>
            <HandPips size="sm" deals={group.map((s) => s.deal ?? (s.state === 'current' ? 'current' : null))} />
            <span className={`flex items-center gap-1 text-[10px] font-semibold tracking-wide ${isCurrent ? 'text-ink-soft' : 'opacity-80'}`}>
              <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: isCurrent ? team.color : 'currentColor' }} aria-hidden />
              {range}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function HistoryTable({ deals, seats, teams }: { deals: readonly DealRecord[]; seats: SeatPlayer[]; teams: Teams }) {
  if (deals.length === 0) {
    return <p className="py-6 text-center text-ink-soft">No deals recorded yet.</p>;
  }
  return (
    <div className="-mx-1 overflow-x-auto">
      {/* Fixed layout with explicit column shares: Result (the point of the table) always
          keeps its room, even with long team names — the other columns truncate instead. */}
      <table className="w-full table-fixed text-sm">
        <caption className="sr-only">Deal history</caption>
        <colgroup>
          <col className="w-7" />
          <col className="w-[22%]" />
          <col className="w-[30%]" />
          <col className="w-9" />
          <col />
        </colgroup>
        <thead>
          <tr className="border-b border-ink/15 text-left text-xs tracking-wide text-ink-soft uppercase">
            <th scope="col" className="px-1 py-2">#</th>
            <th scope="col" className="px-1 py-2">Dealer</th>
            <th scope="col" className="px-1 py-2">Rang</th>
            <th scope="col" className="px-1 py-2 text-center">Suit</th>
            <th scope="col" className="px-1 py-2 text-right">Result</th>
          </tr>
        </thead>
        <tbody>
          {/* Newest deal first: it's the one you're most likely checking or undoing. */}
          {[...deals].reverse().map((d) => {
            const rang = d.trumpTeam ? teams[d.trumpTeam] : null;
            const given = d.guaaA + d.guaaB;
            return (
              <tr key={d.dealNo} className="border-b border-ink/8 last:border-0">
                <td className="px-1 py-2 font-semibold tabular-nums">{d.dealNo}</td>
                <td className="truncate px-1 py-2">
                  <Name>{seats[d.dealerSeat]!.name}</Name>
                </td>
                <td className="px-1 py-2">
                  {rang ? (
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: rang.color }} aria-hidden />
                      <span className="truncate">{rang.name}</span>
                    </span>
                  ) : (
                    <span className="text-ink-soft">–</span>
                  )}
                </td>
                <td className="px-1 py-2 text-center text-lg">
                  {d.trumpSuit ? <SuitGlyph suit={d.trumpSuit} /> : <span className="text-ink-soft" aria-label="not recorded">–</span>}
                </td>
                <td className="px-1 py-2 text-right font-semibold whitespace-nowrap">
                  {d.result === 'saved' ? (
                    <span className="text-success-700">✓ Saved</span>
                  ) : (
                    <span className="text-suit-red">
                      +{given} Guaa{d.isCourt && <span className="ml-1 rounded bg-suit-red px-1 text-[10px] text-white">COURT</span>}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
